import type {
  EsgDashboardByBranchResponse,
  EsgDashboardMonthlyResponse,
} from '../types/esg-dashboard.type';
import { safeNumber } from './reportFormatters';

/** Sum of platform-recorded incoming waste amounts when monthly breakdown rows exist. */
export function deriveIncomingWasteTotalKg(
  monthly: EsgDashboardMonthlyResponse | null
): number | null {
  if (!monthly?.incoming_waste_breakdown?.length) return null;
  let sum = 0;
  for (const row of monthly.incoming_waste_breakdown) {
    const a = safeNumber(row.amount);
    if (a === null) return null;
    sum += a;
  }
  return sum;
}

export function deriveTotalUtilityCostSar(
  monthly: EsgDashboardMonthlyResponse | null
): number | null {
  if (!monthly) return null;
  const activityLines = monthly.scope_emissions?.activity_lines ?? [];
  const parts = activityLines.length > 0
    ? activityLines.map((line) => line.amount_sar)
    : [
        monthly.water_amount_sar,
        monthly.electricity_amount_sar,
        monthly.oil_amount_sar,
        monthly.gas_amount_sar,
      ];
  let sum = 0;
  for (const p of parts) {
    const n = safeNumber(p);
    if (n === null) return null;
    sum += n;
  }
  return sum;
}

export type GriCoverageRow = {
  topic: string;
  evidence: string;
  gap: string;
};

export function buildGri306CoverageRows(
  monthly: EsgDashboardMonthlyResponse | null,
  byBranch: EsgDashboardByBranchResponse | null
): GriCoverageRow[] {
  const incoming = deriveIncomingWasteTotalKg(monthly);
  const hasBreakdown =
    !!monthly?.recycled_waste_breakdown_by_type &&
    monthly.recycled_waste_breakdown_by_type.length > 0;
  const hasBranches = !!byBranch?.branches?.length;

  return [
    {
      topic: 'Waste generation (mass flows)',
      evidence:
        incoming !== null
          ? 'Incoming waste breakdown totals are present for the reporting month.'
          : 'No incoming waste breakdown total derived for the reporting month.',
      gap:
        incoming === null
          ? 'Cannot quantify total generation from breakdown alone without complete incoming series.'
          : 'Downstream destinations beyond platform-recorded flows may be incomplete.',
    },
    {
      topic: 'Waste diverted from disposal (GRI 306-4)',
      evidence: monthly
        ? `Collected mass and pickup completion rate ${
            safeNumber(monthly.total_waste_diverted_kg) !== null ? 'appear' : 'may be incomplete'
          } in monthly figures.`
        : 'Monthly figures were not available for this export.',
      gap:
        'Collected mass includes disposal-destination and rejected loads; recovery operations are not recorded, so GRI 306-4 diversion is not stated.',
    },
    {
      topic: 'Circularity / recycling',
      evidence: hasBreakdown
        ? 'Collected waste breakdown by type and material is available.'
        : 'Composition detail is empty or not provided.',
      gap: 'A recycling-only share (SASB IF-WM) and disposal mass (GRI 306-5) need recovery and disposal operations — not tracked here.',
    },
    {
      topic: 'Spatial / site disaggregation',
      evidence: hasBranches
        ? 'Branch-level monthly rows are available.'
        : 'Branch-level rows are missing.',
      gap: 'Unassigned waste may exist — review unassigned row when present.',
    },
  ];
}
